import { FC } from "react"

import { useTranslation } from "react-i18next"

import { LabeledInput } from "components/inputs/LabeledInput"
import { SectionCard } from "components/settings/SectionCard"

import { LiveChatSettings } from "types/api"

import { ProviderFieldGroup } from "./fields"
import { FormState, SetFormField } from "./types"
import { FormErrors } from "./validation"

interface ProviderCredentialsSectionProps {
  group: ProviderFieldGroup
  form: FormState
  loaded: LiveChatSettings | null
  errors: FormErrors
  saving: boolean
  /** Shown when the provider's identifiers are blank and the frontend env will supply them. */
  showEnvNotice: boolean
  onFieldChange: SetFormField
}

export const ProviderCredentialsSection: FC<ProviderCredentialsSectionProps> = ({
  group,
  form,
  loaded,
  errors,
  saving,
  showEnvNotice,
  onFieldChange
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const base = `settings.liveChat.credentials.${group.provider}`

  const secretHint = (stored?: boolean, envAvailable?: boolean): string => {
    if (stored) return t("settings.liveChat.credentials.secretStored")
    if (envAvailable) return t("settings.liveChat.credentials.secretFromEnv")
    return t("settings.liveChat.credentials.secretUnset")
  }

  return (
    <SectionCard title={t(`${base}.title`)} description={t(`${base}.description`)}>
      {showEnvNotice && (
        <p className="text-xs text-warning">{t("settings.liveChat.credentials.envNotice")}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {group.fields.map((field) => {
          const stored = field.storedFlag && loaded?.secretsPresent?.[field.storedFlag]
          const envAvailable = field.envFlag && loaded?.envFallbacks?.[field.envFlag]
          const error = errors[field.key]

          return (
            <LabeledInput
              key={field.key}
              id={`live-chat-${group.provider}-${field.key}`}
              label={t(`${base}.${field.labelKey}`)}
              value={form[field.key] as string}
              onChange={(value) => onFieldChange(field.key, value as FormState[typeof field.key])}
              disabled={saving}
              revealable={field.secret}
              autoComplete={field.secret ? "one-time-code" : undefined}
              placeholder={
                field.placeholder ??
                t(field.secret ? "settings.liveChat.credentials.notSet" : "settings.liveChat.credentials.fromEnv")
              }
              error={error && t(`settings.liveChat.validation.${error}`)}
              hint={field.secret ? secretHint(stored, envAvailable) : field.hintKey && t(`${base}.${field.hintKey}`)}
            />
          )
        })}
      </div>
    </SectionCard>
  )
}
